import React, { useMemo } from 'react'
import { ChargingSessionModel } from '../../../charging-sessions/models/charging-sessions.model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material'
import { formatDuration } from '@e3dc-react/shell/Datetime'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { MIN_STATISTIC_CARD_WIDTH, StatisticCard } from './StatisticCard'
import { CUSTOM_TABLET_BREAKPOINT, MetricType } from 'src/Libs/e-mobility/dashboard/models/dashboard.model'
import { sum } from 'lodash'
import { getCurrencyValue } from 'src/Libs/e-mobility/shared'
import { AZElectricity, B3Photovoltaic, BZTime, B3Comfortmode } from '@e3dc-react/icons'

interface StatisticCardConfiguration {
  title: string
  icon: React.ReactElement
  value: string
  hidden?: boolean
}

interface StatisticCardsProps {
  sessions: ChargingSessionModel[]
  includesBiDiSessions: boolean
  metric: MetricType
  currencyCode: string
}

const PERCENTAGE_MULTIPLIER = 100

/**
 * Statistic cards component all statistic card inside dashboard and switches from
 * desktop view where all cards are shown in a continuous flexbox to mobile view where
 * a single card is shown, BUT with the dropdown menu from where any other card can be
 * selected.
 *
 * It is responsible for aggregations of all related data and values
 *
 * @param - sessions as ChargingSessionsModel array
 */
export const StatisticCards: React.FC<StatisticCardsProps> = ({ sessions, includesBiDiSessions, metric, currencyCode }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(CUSTOM_TABLET_BREAKPOINT))
  const isExtraSMall = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useCustomTranslation()

  //calculate total values for metric type "energy"
  const totalChargeTime = sessions.reduce((a, b) => a + b.durationMilliseconds, 0)
  const totalGridEnergyKWh = sessions.reduce((a, b) => a + b.gridEnergyKWh, 0)
  const totalSolarEnergyKWh = sessions.reduce((a, b) => a + b.energySolarKWh, 0)
  const totalDischargedEnergyKWh = sessions.reduce((a, b) => a + b.energyDischargedKWh, 0)

  // calculate total values for metric type "cost"
  const totalCost = sum(sessions.map(session => (session.totalCost ? session.totalCost : 0)))
  const totalGridCost = sum(sessions.map(session => (session.gridCost ? session.gridCost : 0)))
  const totalSolarCost = sum(sessions.map(session => (session.solarCost ? session.solarCost : 0)))
  const totalDischargeCost = sum(sessions.map(session => (session.totalDischargeCost ? session.totalDischargeCost : 0)))

  let totalSolarRatio: number

  if (totalSolarEnergyKWh === 0) {
    /** first we check if solar energy is 0, because we can't divide anything with 0, then solar ratio will be 0% */
    totalSolarRatio = 0
  } else if (totalGridEnergyKWh === 0) {
    /** So if we have solar energy but we don't have grid energy, then solar ratio is 100% */
    totalSolarRatio = 100
  } else {
    /** if we have both grid and solar energy, we calculate proper solar ratio  */
    totalSolarRatio = (totalSolarEnergyKWh / (totalGridEnergyKWh + totalSolarEnergyKWh)) * PERCENTAGE_MULTIPLIER
  }

  const fractionDigits = isExtraSMall ? 0 : 2

  // format values for metric type "energy", display values with kwh
  const formattedDuration = formatDuration(totalChargeTime, isExtraSMall)
  const formattedTotalGridEnergyKWh = t('common.format.kwh', { value: getLocalizedNumber(totalGridEnergyKWh, fractionDigits) })
  const formattedTotalSolarEnergyKWh = t('common.format.kwh', { value: getLocalizedNumber(totalSolarEnergyKWh, fractionDigits) })
  const formattedTotalDischargeEnergyKWh = t('common.format.kwh', { value: getLocalizedNumber(totalDischargedEnergyKWh, fractionDigits) })
  const formattedSolarRatio = t('common.format.percentageValue', { value: getLocalizedNumber(totalSolarRatio) })

  // format values for metric type "cost", display values with currency code
  const formattedTotalCost = getCurrencyValue(totalCost, currencyCode)
  const formattedGridCost = getCurrencyValue(totalGridCost, currencyCode)
  const formattedTotalSolarCost = getCurrencyValue(totalSolarCost, currencyCode)
  const formattedTotalDischargedCost = getCurrencyValue(totalDischargeCost, currencyCode)

  const statisticCardConfigurations: StatisticCardConfiguration[] = useMemo(
    () => [
      {
        title:
          metric === MetricType.Energy
            ? t('emobility.chargingSession.total-grid-energy-charged')
            : t('emobility.chargingSession.total-grid-energy-cost'),
        value: metric === MetricType.Energy ? formattedTotalGridEnergyKWh : formattedTotalCost,
        icon: <AZElectricity fontSize="large" />,
      },
      {
        title:
          metric === MetricType.Energy ? t('emobility.chargingSession.totalSolarEnergyCharged') : t('emobility.chargingSession.grid-energy-cost'),
        icon: <B3Photovoltaic fontSize="large" />,
        value: metric === MetricType.Energy ? formattedTotalSolarEnergyKWh : formattedGridCost,
      },
      {
        title: t('emobility.chargingSession.charge-duration-total'),
        value: formattedDuration,
        icon: <BZTime fontSize="large" />,
      },

      {
        icon: <B3Comfortmode fontSize="large" />,
        title: metric === MetricType.Energy ? t('emobility.chargingSession.solarEnergyRatio') : t('emobility.chargingSession.solar-energy-cost'),
        value: metric === MetricType.Energy ? formattedSolarRatio : formattedTotalSolarCost,
      },
    ],
    [
      t,
      formattedDuration,
      metric,
      formattedTotalGridEnergyKWh,
      formattedTotalCost,
      formattedTotalSolarEnergyKWh,
      formattedGridCost,
      formattedSolarRatio,
      formattedTotalSolarCost,
    ]
  )

  if (isMobile) {
    return (
      <Grid id="e-mobility-statistics" container spacing={2} mt={0.5}>
        {statisticCardConfigurations.map(config => {
          return (
            <Grid key={config.title} item xs={6}>
              <StatisticCard icon={config.icon} title={config.title} value={config.value} size="small" />
            </Grid>
          )
        })}
        {includesBiDiSessions && (
          <Grid item xs={12}>
            <StatisticCard
              icon={<AZElectricity fontSize="large" />}
              title={
                metric === MetricType.Energy
                  ? t('emobility.chargingSession.discharge-energy-total')
                  : t('emobility.chargingSession.discharge-energy-cost')
              }
              value={metric === MetricType.Energy ? formattedTotalDischargeEnergyKWh : formattedTotalDischargedCost}
              size="small"
            />
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <Box
      id="e-mobility-statistics"
      mt={2}
      display="grid"
      width={statisticCardConfigurations.length > 1 ? undefined : 'fit-content'}
      gap={2}
      gridTemplateColumns={`repeat(auto-fit, minmax(${MIN_STATISTIC_CARD_WIDTH}px, 1fr))`}
    >
      {statisticCardConfigurations.map(config => {
        return (
          <Box key={config.title}>
            <StatisticCard icon={config.icon} title={config.title} value={config.value} />
          </Box>
        )
      })}
      {includesBiDiSessions && (
        <StatisticCard
          icon={<AZElectricity fontSize="large" />}
          title={
            metric === MetricType.Energy
              ? t('emobility.chargingSession.discharge-energy-total')
              : t('emobility.chargingSession.discharge-energy-cost')
          }
          value={metric === MetricType.Energy ? formattedTotalDischargeEnergyKWh : formattedTotalDischargedCost}
        />
      )}
    </Box>
  )
}
